<template>
  <span class="location-preview-chip" @click.stop>
    <SrpDropdown placement="bottom-start" :offset="4" exclusiveGroup="location-preview-chip">
      <template #clickableElement="{ isDropdownVisible }">
        <button type="button" class="location-preview-chip__trigger" :class="{ 'location-preview-chip__trigger--open': isDropdownVisible }" :aria-expanded="isDropdownVisible" aria-haspopup="dialog">
          <IconEmbedded v-if="props.showTagIcon" name="tag-person_2" :size="17" color="#667085" />
          <span>{{ props.location.locationName }}</span>
        </button>
      </template>
      <template #dropdown="{ close }">
        <div class="location-preview-chip__panel" role="dialog" :aria-label="props.location.locationName">
          <div class="location-preview-chip__rows">
            <a v-if="websiteHref" class="location-preview-chip__row location-preview-chip__row--name" :href="websiteHref" target="_blank" rel="noopener noreferrer">
              <span class="location-preview-chip__icon-slot">
                <LocationAvatar :src="props.location.icon" :name="props.location.locationName" :size="28" />
              </span>
              <span class="location-preview-chip__name-cluster">
                <span class="location-preview-chip__label location-preview-chip__label--name">{{ props.location.locationName }}</span>
                <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
              </span>
            </a>
            <div v-else class="location-preview-chip__row location-preview-chip__row--name">
              <span class="location-preview-chip__icon-slot">
                <LocationAvatar :src="props.location.icon" :name="props.location.locationName" :size="28" />
              </span>
              <span class="location-preview-chip__name-cluster">
                <span class="location-preview-chip__label location-preview-chip__label--name">{{ props.location.locationName }}</span>
              </span>
            </div>
            <template v-for="link in socialLinks" :key="link.platform">
              <a v-if="link.href" class="location-preview-chip__row location-preview-chip__row--social" :href="link.href" target="_blank" rel="noopener noreferrer">
                <span class="location-preview-chip__icon-slot">
                  <IconEmbedded :name="link.platform" :size="22" :color="link.color" />
                </span>
                <span class="location-preview-chip__label">{{ link.label }}</span>
              </a>
              <div v-else class="location-preview-chip__row">
                <span class="location-preview-chip__icon-slot">
                  <IconEmbedded :name="link.platform" :size="16" :color="link.color" />
                </span>
                <span class="location-preview-chip__label">{{ link.label }}</span>
              </div>
            </template>
            <div v-if="!socialLinks.length" class="location-preview-chip__empty">No social handles set</div>
            <div v-else-if="!props.showTagIcon && props.hasCalendarSocialAccounts" class="location-preview-chip__empty location-preview-chip__empty--with-icon">
              <IconEmbedded name="warning_2-5" :size="14" color="#667085" />
              <span>
                Tagging turned off —
                <button type="button" class="location-preview-chip__enable-link" :disabled="props.isEditDisabled" @click.stop="handleEdit(close)">edit to enable</button>
              </span>
            </div>
          </div>
          <button v-if="props.isEditVisible" type="button" class="location-preview-chip__edit" aria-label="Edit post" :disabled="props.isEditDisabled" @click.stop="handleEdit(close)">
            <IconEmbedded name="pencil_2" :size="15" color="#667085" />
          </button>
        </div>
      </template>
    </SrpDropdown>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import type { IconName } from "@contracts/IconName";
import type { PostLocationDisplayItem } from "@contracts/socialLocations";
import { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl, buildWebsiteUrl } from "@logic/SocialFieldInputCleaner";

interface SocialLink {
  platform: IconName;
  label: string;
  href: string;
  color: string;
}

const props = withDefaults(
  defineProps<{
    location: PostLocationDisplayItem;
    showTagIcon?: boolean;
    hasCalendarSocialAccounts?: boolean;
    isEditVisible?: boolean;
    isEditDisabled?: boolean;
  }>(),
  {
    showTagIcon: true,
    hasCalendarSocialAccounts: false,
    isEditVisible: false,
    isEditDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "editPost"): void;
}>();

const websiteHref = computed(() => buildWebsiteUrl(props.location.website));

const socialLinks = computed((): SocialLink[] => {
  const links: SocialLink[] = [];
  const instagramHandle = props.location.instagramHandle?.trim();
  if (instagramHandle) {
    links.push({
      platform: "instagram",
      label: `@${instagramHandle.replace(/^@/, "")}`,
      href: buildInstagramProfileUrl(instagramHandle),
      color: "#d13870",
    });
  }
  const facebookName = props.location.facebookPageName?.trim();
  const facebookHref = buildFacebookPageUrl(props.location.facebookPageUrl, props.location.facebookPageId);
  if (facebookName || facebookHref) {
    links.push({
      platform: "facebook",
      label: facebookName || "Facebook",
      href: facebookHref,
      color: "#1773ea",
    });
  }
  const tikTokHandle = props.location.tikTokHandle?.trim();
  if (tikTokHandle) {
    links.push({
      platform: "tiktok",
      label: `@${tikTokHandle.replace(/^@/, "")}`,
      href: buildTikTokProfileUrl(tikTokHandle),
      color: "#000500",
    });
  }
  return links;
});

function handleEdit(close: () => void) {
  if (props.isEditDisabled) return;
  close();
  emit("editPost");
}
</script>

<style scoped lang="scss">
.location-preview-chip {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;

  :deep(.dropdown-wrapper),
  :deep(.dropdown-wrapper__clickable-element) {
    max-width: 100%;
    min-width: 0;
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 4px 8px 4px 6px;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    background: #fff;
    color: #344054;
    font: 12px/16px sans-serif;
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      background-color 0.12s ease,
      box-shadow 0.12s ease;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:hover,
    &:focus-visible,
    &--open {
      border-color: #98a2b3;
      background: #f2f4f7;
      box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #98a2b3;
      outline-offset: 1px;
    }
  }

  &__panel {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 250px;
    max-width: min(420px, calc(100vw - 24px));
    padding: 10px 10px 10px 12px;
    border: 1px solid #d0d5dd;
    border-radius: 5px;
  }

  &__rows {
    display: grid;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: #344054;
    font: 13px/18px sans-serif;
    text-decoration: none;

    &--name:hover .location-preview-chip__label,
    &--social:hover .location-preview-chip__label {
      text-decoration: underline;
    }
  }

  &__icon-slot {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__name-cluster {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--name {
      font-size: 15px;
      line-height: 20px;
      font-weight: 600;
    }
  }

  &__empty {
    color: #667085;
    font-size: 12px;
    line-height: 16px;
    padding-left: 36px;

    &--with-icon {
      display: flex;
      align-items: center;
      gap: 5px;
    }
  }

  &__enable-link {
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-decoration: underline;
    text-decoration-style: dashed;
    text-decoration-color: rgba(91, 91, 91, 0.5);
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover:not(:disabled) {
      text-decoration: none;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__edit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      background-color 0.12s ease;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      border-color: #98a2b3;
      background: #f2f4f7;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
